import * as qs from 'qs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ProgressBar } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { X } from 'react-bootstrap-icons';
import { makeApiRequests } from '../../helpers/api';
import {
  docHubSourceOptions,
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_INVENTORY_MANAGER,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN,
  ROLE_TECH
} from '../../helpers/constants';
import { getDocIdFromUrl } from '../../helpers/global';
import { getUserRoles, userHasAnyRole } from '../../helpers/roleHelpers';
import Loader from '../Loader';
import FilePopup from '../common/FilePopup';
import SelectionBox from '../doc-hub/PrintGroupSelectionBox';
import DocumentPrinter from '../doc-hub/DocumentPrinter';
import DocumentPrinterV2 from './DocumentPrinterV2';
import './PrintGroupsV2.scss';
import { isCustomerChecklist, documentPresentationVersion } from './checklistPresentation';

const DEFAULT_DOCHUB_VERSION = 'v2';

const normalizeDocHubVersion = version => (version === 'v1' ? 'v1' : DEFAULT_DOCHUB_VERSION);

/** Can pick Contract vs Contact (etc.) source; includes Inventory Manager */
const userHasFullDocHubSourceAccess = () =>
  userHasAnyRole(
    ROLE_SUPER_ADMIN,
    ROLE_ADMIN,
    ROLE_SALES,
    ROLE_SALES_MANAGER,
    ROLE_INVENTORY_MANAGER,
    ROLE_SALES_AND_INVENTORY_MANAGER
  );

/** Doc source dropdown in UI — same as before, excludes Inventory Manager-only users */
const userShowsDocSourceSelector = () =>
  userHasAnyRole(
    ROLE_SUPER_ADMIN,
    ROLE_ADMIN,
    ROLE_SALES,
    ROLE_SALES_MANAGER,
    ROLE_SALES_AND_INVENTORY_MANAGER
  );

const userCanSyncDocumentLayout = () => userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_TECH);

const getSource = source => {
  if (userHasFullDocHubSourceAccess() && docHubSourceOptions.includes(source)) {
    return source;
  }
  const lowered = getUserRoles().map(r => String(r).toLowerCase());
  const idx = lowered.some(r => r.includes('sales')) ? 1 : lowered.some(r => r.includes('inventory')) ? 2 : 0;
  return docHubSourceOptions[idx];
};

const getDocumentLists = (appChoices, docSource) => {
  let printGroupResults = [];
  const allPrintGroups = appChoices.find(ac => ac.key === 'printGroups').values;
  const userRolesLower = getUserRoles().map(r => String(r).toLowerCase());

  let filteredPrintGroups = allPrintGroups
    .filter(
      pg =>
        (userHasFullDocHubSourceAccess() ||
          pg.roles.some(pr => userRolesLower.includes(String(pr).toLowerCase()))) &&
        pg.dataSources.includes(docSource)
    )
    .map(pg => ({
      _id: pg._id,
      name: pg.name,
      url: pg.url,
      category: pg?.category || 'Others',
      defaultDocHubVersion: normalizeDocHubVersion(pg.defaultDocHubVersion)
    }));

  const uniquePrintCategories = new Set(filteredPrintGroups.map(pg => pg?.category || 'Others'));

  Array.from(uniquePrintCategories).forEach(upc => {
    let printGroupByCategory = filteredPrintGroups.filter(fpg => fpg?.category === upc);
    printGroupByCategory.unshift({ categoryLabel: upc });

    printGroupResults.push(...printGroupByCategory);
  });

  return printGroupResults;
};

const getDocumentFieldsFromParams = params => {
  return {
    withData: params.filter(p => p.value),
    withoutData: params.filter(p => !p.value)
  };
};

const PrintGroupsV2 = ({ appChoices, source, contractId, contactId, document, sidebarMode, onUpdateDocHub }) => {
  const location = useLocation();
  const canSyncLayout = useMemo(() => userCanSyncDocumentLayout(), []);

  const {
    source: sourceFromUrl,
    contractId: contractIdFromUrl,
    contactId: contactIdFromUrl,
    document: documentFromUrl
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const [docSource, setDocSource] = useState(getSource(sourceFromUrl || source));
  const [documents, setDocuments] = useState([]);
  const [docHubVersion, setDocHubVersion] = useState(DEFAULT_DOCHUB_VERSION);

  const [selectedGroup, setSelectedGroup] = useState();
  const [loading, setLoading] = useState(false);

  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState();
  const [documentFieldsLoading, setDocumentFieldsLoading] = useState(false);
  const [documentFields, setDocumentFields] = useState(null);
  const [documentLayout, setDocumentLayout] = useState(null);
  const [documentLayoutLoading, setDocumentLayoutLoading] = useState(false);
  const [layoutStatus, setLayoutStatus] = useState(null);
  const [syncingLayout, setSyncingLayout] = useState(false);

  const [printingBlankDocument, setPrintingBlankDocument] = useState(false);
  const [printedBlankDocument, setPrintedBlankDocument] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);

  const selectedDocument = useMemo(
    () => documents.find(d => d.url === selectedDocumentUrl),
    [documents, selectedDocumentUrl]
  );
  const isV2 = isCustomerChecklist(selectedDocument?.name) || docHubVersion === 'v2';

  const applyVersionFromDocument = useCallback(doc => {
    if (!doc) return;
    setDocHubVersion(documentPresentationVersion(doc));
  }, []);

  useEffect(() => {
    loadContractOrContactIfPreselected();
  }, [contactId, contractId, document, sidebarMode]);

  useEffect(() => {
    const documentList = getDocumentLists(appChoices, docSource);

    setDocuments(documentList);
    const firstDoc = documentList.find(d => d?.url);
    setSelectedDocumentUrl(firstDoc?.url);
    applyVersionFromDocument(firstDoc);
  }, [docSource, appChoices, applyVersionFromDocument]);

  const fetchLayoutStatus = useCallback(async docId => {
    if (!docId) {
      setLayoutStatus(null);
      return;
    }

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_DOCUMENT_LAYOUT_STATUS,
      requestBody: { docId }
    });

    if (error) {
      setLayoutStatus({ docId, status: 'never' });
      return;
    }

    setLayoutStatus(response);
  }, []);

  useEffect(() => {
    if (!isV2) {
      setLayoutStatus(null);
      return;
    }
    const docId = getDocIdFromUrl(selectedDocumentUrl);
    fetchLayoutStatus(docId);
  }, [selectedDocumentUrl, fetchLayoutStatus, isV2]);

  const loadContractOrContactIfPreselected = async () => {
    if (
      (docSource === 'Contract' && (contractIdFromUrl || contractId)) ||
      (docSource === 'Contact' && (contactIdFromUrl || contactId))
    ) {
      loadDetails({
        source: docSource,
        contractId: contractIdFromUrl || contractId,
        contactId: contactIdFromUrl || contactId,
        preSelectedDocument: documentFromUrl || document
      });
    }
  };

  const loadDetails = async ({ source, contractId, contactId, preSelectedDocument }) => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: source !== 'Contact' ? ENDPOINTS.CONTRACTS_SEARCH : ENDPOINTS.CONTACTS_SEARCH,
      requestBody: { filter: { _id: source !== 'Contact' ? contractId : contactId } }
    });

    if (error || response.length === 0) {
      setLoading(false);
      return;
    }

    const details = response[0];
    setSelectedGroup(details);

    const existingDocument = getDocumentLists(appChoices, docSource).find(d => {
      if (!d._id || !d.name) return false;
      const queryName = (preSelectedDocument || '').trim().toLowerCase();
      if (!queryName) return false;
      return d.name.trim().toLowerCase() === queryName;
    });

    if (existingDocument) {
      setSelectedDocumentUrl(existingDocument.url);
      applyVersionFromDocument(existingDocument);
      getDocumentFields(
        details['_id'],
        existingDocument.url,
        documentPresentationVersion(existingDocument)
      );
    }

    setLoading(false);
  };

  const onGroupPicked = group => {
    setSelectedGroup(group);
    clearCurrentForm();
  };

  const onDocumentChange = url => {
    setSelectedDocumentUrl(url || '');
    const doc = documents.find(d => d.url === url);
    applyVersionFromDocument(doc);
  };

  const onDocHubVersionChange = version => {
    setDocHubVersion(normalizeDocHubVersion(version));
    clearCurrentForm();
  };

  const onSourceChange = e => {
    const value = e ? e.target.value : document.getElementById('sourceSelect').value;
    if (value === 'User') { window.location.assign(userHasAnyRole(ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER) ? '/onboarding' : '/onboarding/settings'); return; }
    setDocSource(value);
    setSelectedGroup(null);
  };

  useEffect(() => clearCurrentForm(), [docSource, selectedDocumentUrl]);

  const clearCurrentForm = () => {
    setDocumentFields(null);
    setDocumentLayout(null);
  };

  const getDocumentFields = async (id = '', preSelectedDocumentUrl = '', versionOverride) => {
    clearCurrentForm();
    setDocumentFieldsLoading(true);

    const docId = getDocIdFromUrl(preSelectedDocumentUrl || selectedDocumentUrl);
    const requestedDocument = documents.find(d => d.url === (preSelectedDocumentUrl || selectedDocumentUrl));
    const version = documentPresentationVersion(requestedDocument, versionOverride || docHubVersion);
    const fetchLayout = version === 'v2';

    if (fetchLayout) {
      setDocumentLayoutLoading(true);
    }

    const paramsRequest = makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_DOCUMENT_PARAMS(id || selectedGroup['_id']),
      requestBody: {
        docId,
        dataSource: docSource
      }
    });

    const layoutRequest = fetchLayout
      ? makeApiRequests({
          endpoint: ENDPOINTS.DOCHUB_DOCUMENT_LAYOUT,
          requestBody: { docId }
        })
      : Promise.resolve({ response: null, error: null });

    const [paramsResult, layoutResult] = await Promise.all([paramsRequest, layoutRequest]);

    setDocumentFieldsLoading(false);
    setDocumentLayoutLoading(false);

    if (paramsResult.error) {
      return toast.error(paramsResult.error);
    }

    if (fetchLayout && layoutResult.error) {
      return toast.error(layoutResult.error);
    }

    setDocumentFields(getDocumentFieldsFromParams(paramsResult.response.foundParams));
    setDocumentLayout(fetchLayout ? layoutResult.response : null);
  };

  const syncDocumentLayout = async () => {
    const docId = getDocIdFromUrl(selectedDocumentUrl);
    if (!docId) {
      toast.error('Could not resolve document id from URL');
      return;
    }

    setSyncingLayout(true);
    toast.info('Syncing document layout from Google Sheets… this can take several minutes.');

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_DOCUMENT_LAYOUT_SYNC,
      requestBody: { docId }
    });

    setSyncingLayout(false);

    if (error) {
      toast.error(error);
      fetchLayoutStatus(docId);
      return;
    }

    setLayoutStatus({
      docId: response.docId,
      status: response.status,
      syncInProgress: response.syncInProgress,
      lastSyncedAt: response.lastSyncedAt,
      lastSyncError: response.lastSyncError,
      spreadsheetName: response.spreadsheetName,
      sheetCount: response.sheetCount,
      lastSyncDurationMs: response.lastSyncDurationMs
    });

    toast.success(
      `Layout synced${response.sheetCount != null ? ` (${response.sheetCount} sheets)` : ''}${
        response.lastSyncDurationMs != null ? ` in ${Math.round(response.lastSyncDurationMs / 1000)}s` : ''
      }`
    );
  };

  const printBlankDocument = async () => {
    let requestOptions = {
      endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT,
      requestBody: {
        documentParams: {},
        docId: getDocIdFromUrl(selectedDocumentUrl),
        documentName: documents.find(d => d.url === selectedDocumentUrl).name,
        dataSource: 'Mock',
        printSessionId: uuid()
      }
    };

    setPrintingBlankDocument(true);

    toast.info('Please wait, creating document...');

    const { error, response } = await makeApiRequests({ ...requestOptions });

    setPrintingBlankDocument(false);

    if (error) {
      toast(error, {
        type: 'error'
      });
      return;
    }

    setPrintedBlankDocument({ originalName: response['metadata']['originalName'], ...response });
    setPrintingBlankDocument(false);
  };

  const onSwitchToExportedContractClick = () => {
    const exportedContract = selectedGroup?.exportedContract;
    if (!exportedContract) return;

    setDocSource('Contract');
    setSelectedGroup(exportedContract);
  };

  const isServiceRequest = useMemo(() => {
    if (documents) {
      const serviceRequestDocumentsUrls = documents.filter(d => d?.name?.includes('Service Request')).map(d => d.url);
      if (serviceRequestDocumentsUrls) {
        return serviceRequestDocumentsUrls.includes(selectedDocumentUrl);
      }
    }

    return false;
  }, [documents, selectedDocumentUrl]);

  const documentName = selectedDocument?.name;
  const showPreviewLoading = documentFieldsLoading || (isV2 && documentLayoutLoading);
  const showV2Printer = isV2 && documentLayout && !showPreviewLoading;
  const showV1Printer = !isV2 && documentFields && !documentFieldsLoading;

  return (
    <div className="px-2 px-md-5 py-2 ">
      {loading ? (
        <Loader />
      ) : (
        <>
          {!sidebarMode && (
            <>
              <SelectionBox
                showDocSourceSelector={userShowsDocSourceSelector()}
                docSource={docSource}
                onDocSourceChange={onSourceChange}
                selectedGroup={selectedGroup}
                onGroupPicked={onGroupPicked}
                selectedDocument={selectedDocumentUrl}
                documents={documents}
                onDocumentChange={onDocumentChange}
                onPreviewClick={() => getDocumentFields()}
                canSyncLayout={canSyncLayout}
                onSyncLayoutClick={syncDocumentLayout}
                layoutStatus={layoutStatus}
                syncingLayout={syncingLayout}
                onSwitchToExportedContractClick={onSwitchToExportedContractClick}
                documentFieldsLoading={showPreviewLoading}
                printingBlankDocument={printingBlankDocument}
                onPrintBlankDocumentClick={() => printBlankDocument()}
                docHubVersion={docHubVersion}
                onDocHubVersionChange={isCustomerChecklist(documentName) ? undefined : onDocHubVersionChange}
              />
              <hr />
            </>
          )}
          {printedBlankDocument && (
            <>
              <Alert variant="success" className="my-3 d-flex align-items-center">
                <p className="mb-0 flex-grow-1">
                  A Blank Document "{printedBlankDocument?.originalName}" has been created successfully. You may quickly
                  access the file here.
                </p>

                <Button className="mx-2" variant="success" onClick={() => setViewingFile(printedBlankDocument)}>
                  Open File
                </Button>
                <X size={25} className="ml-2 hover" onClick={() => setPrintedBlankDocument(null)} />
              </Alert>
              {viewingFile && <FilePopup file={viewingFile} onHide={() => setViewingFile(null)} />}
            </>
          )}
          {showPreviewLoading && <ProgressBar className="mb-2" now={100} animated label="Loading document..." />}
          {showV2Printer && (
            <DocumentPrinterV2
              appChoices={appChoices}
              docSource={docSource}
              documentId={getDocIdFromUrl(selectedDocumentUrl)}
              documentUrl={selectedDocumentUrl}
              documentName={documentName}
              documentFields={documentFields}
              documentLayout={documentLayout}
              isServiceRequest={isServiceRequest}
              selectedGroup={selectedGroup}
              onServiceOrderReportUpdate={setSelectedGroup}
              onUpdateDocHub={onUpdateDocHub}
            />
          )}
          {showV1Printer && (
            <DocumentPrinter
              appChoices={appChoices}
              docSource={docSource}
              documentId={getDocIdFromUrl(selectedDocumentUrl)}
              documentName={documentName}
              documentFields={documentFields}
              isServiceRequest={isServiceRequest}
              selectedGroup={selectedGroup}
              onServiceOrderReportUpdate={setSelectedGroup}
              onUpdateDocHub={onUpdateDocHub}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PrintGroupsV2;
